// context/featureFlagsProvider.jsx
import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { loadFlags, resolveFlagFrom } from '../utility/featureFlags';

const Ctx = createContext({
    loading: true,
    version: 0,
    flags: {},
    reload: async () => { },
    resolve: () => false,
});

export function FeatureFlagsProvider({ children, currentUser }) {
    const [loading, setLoading] = useState(true);
    const [version, setVersion] = useState(0);
    const [flags, setFlags] = useState({});
    const loadedRef = useRef(false);   // evita doppie chiamate in StrictMode

    const fetchAll = useCallback(async () => {
        if (loadedRef.current) {
            return;
        }
        setLoading(true);
        try {
            const data = await loadFlags();
            setFlags(data?.flags ?? {});
            setVersion(data?.version ?? 0);
            loadedRef.current = true;
        } catch (err) {
            console.error('[FF] loadFlags ERROR:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // ogni volta che cambia utente, ricarico
        loadedRef.current = false;
        fetchAll();
        //utile per debug --> console.log('[FF] user changed ->', currentUser);
    }, [currentUser, fetchAll]);


    // “paracadute”: se qualcuno chiama resolve e non abbiamo ancora caricato, forziamo un lazy-load
    const ensureLoaded = useCallback(async () => {
        if (!loadedRef.current && !loading) {
            await fetchAll();
        }
    }, [fetchAll, loading]);

    const resolve = useCallback(
        (key, def = false) => {
            // in dev: avvisa se stai risolvendo prima del load
            if (!loadedRef.current) {
                // non blocco l’app: provo a dare un valore con flags correnti (vuoti) e in parallelo cerco di caricare
                ensureLoaded();
            }
            const v = resolveFlagFrom(
                flags,
                key,
                {
                    username: currentUser?.email || '',
                    roles: currentUser?.ruoli || [],
                    iduser: currentUser?.userId || '',
                },
                def
            );
            return v;
        },
        [flags, currentUser, ensureLoaded]
    );

    const value = useMemo(
        () => ({ loading, version, flags, reload: fetchAll, resolve }),
        [loading, version, flags, fetchAll, resolve]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFeature(key, def = false) {
    const { resolve } = useContext(Ctx);
    return resolve(key, def);
}

export function useFlags() {
    return useContext(Ctx);
}
